import type { BillingTabKey } from '@/app/router/paths';
import type { CreditReferenceType } from '@/entities/credit';
import type { BillingIntegration, InvoiceBatchResult } from '@/entities/invoice';
import { formatNumber } from '@/shared/lib';

/**
 * Tegoed telt in hele keuringen, nooit in euro's (`docs/woordenlijst.md`): `25 keuringen`,
 * `1 keuring`. Met `signed` krijgt een boeking haar teken: `+25 keuringen`, `−3 keuringen`.
 * In de API heet die eenheid een credit; de klant leest keuringen.
 */
export function formatKeuringen(value: number, options: { signed?: boolean } = {}): string {
    const size = Math.abs(value);
    const sign = value < 0 ? '−' : options.signed && value > 0 ? '+' : '';

    return `${sign}${formatNumber(size)} ${size === 1 ? 'keuring' : 'keuringen'}`;
}

/** Het getal in de kolom Keuringen van het grootboek: `+50`, `−4`, `0`. */
export function formatCreditDelta(value: number): string {
    const sign = value < 0 ? '−' : value > 0 ? '+' : '';

    return `${sign}${formatNumber(Math.abs(value))}`;
}

const invoices = (n: number) => (n === 1 ? 'factuur' : 'facturen');

/**
 * Nederlandse teksten van Facturatie. Jij-vorm, labels zijn zelfstandige
 * naamwoorden, knoppen werkwoorden. Wat de klant bestelt heet een keuring, het
 * keuringsbedrijf een bedrijf; tegoed telt in keuringen.
 */
export const billingCopy = {
    title: 'Facturatie & tarieven',
    eyebrowLoading: 'Deze maand',
    eyebrow: (periodLabel: string, integration: BillingIntegration | null) =>
        integration
            ? `${periodLabel} · ${integration.providerLabel ?? 'Boekhouding'}: ${integration.statusLabel}`
            : periodLabel,
    loading: (title: string) => `${title} laden`,
    tabsLabel: 'Facturatie',

    /* Fouten */
    errorOffline: 'Je bent offline. Dit lukt alleen met verbinding.',
    errorOfflineRead: 'Je bent offline en dit is nog niet eerder op dit apparaat geladen.',
    errorForbidden: 'Je hebt hier geen toestemming voor.',
    errorNotFound: 'Dit bestaat niet meer. Ververs de lijst en probeer het opnieuw.',
    errorSession: 'Je sessie is verlopen. Log opnieuw in.',
    errorServer: 'Er ging iets mis op de server. Probeer het zo opnieuw.',
    errorRateLimited: 'Te veel verzoeken achter elkaar. Probeer het zo opnieuw.',

    /* Versturen in één actie */
    sendAll: (n: number) => `${n} ${invoices(n)} versturen`,
    sendAllConfirmLabel: 'Bevestig versturen van alle concepten',
    sendAllConfirm: (n: number) =>
        `${n === 1 ? 'Het concept krijgt' : `Alle ${n} concepten krijgen`} een factuurnummer en een factuurdatum, en de PDF wordt gemaakt. Daarna kun je ${n === 1 ? 'de factuur' : 'ze'} niet meer wijzigen.`,
    sendAllPartial: (sending: number, total: number) =>
        `Dit zijn de eerste ${sending} van ${total} concepten; de rest verstuur je daarna.`,
    sendAllYes: (n: number) => `Ja, ${n} ${invoices(n)} versturen`,
    sendAllFailed: 'Versturen is niet gelukt',
    batchTitleComplete: (sent: number) => `${sent} ${invoices(sent)} verstuurd`,
    batchTitlePartial: (sent: number, requested: number) =>
        `${sent} van ${requested} ${invoices(requested)} verstuurd`,
    batchComplete: 'Elke factuur heeft nu een nummer en een factuurdatum.',
    batchPartial: (notSent: number) =>
        `${notSent === 1 ? 'Eén factuur bleef' : `${notSent} facturen bleven`} liggen. Hieronder staat waarom.`,
    batchResult: {
        sent: 'Verstuurd',
        failed: 'Niet verstuurd',
        forbidden: 'Geen toestemming',
        not_found: 'Niet gevonden',
    } satisfies Record<InvoiceBatchResult, string>,
    batchUnknownReason: 'Geen reden bekend.',
    batchRetry: (n: number) => `${n} opnieuw proberen`,
    close: 'Sluiten',
    cancel: 'Annuleren',

    /* Facturen */
    invoicesTitle: 'Facturen',
    invoicesTableLabel: 'Facturen',
    filterStatus: 'Status',
    filterStatusAll: 'Alle statussen',
    filterOutstanding: 'Openstaand',
    filterNumber: 'Factuurnummer',
    filterNumberPlaceholder: 'Bijvoorbeeld 2026-0141',
    columnNumber: 'Nr',
    columnObject: 'Object',
    columnClient: 'Klant',
    columnStatus: 'Status',
    columnAmount: 'Bedrag',
    invoicesNoun: 'facturen',
    invoicesEmpty: 'Nog geen facturen.',
    invoicesEmptyFiltered: 'Geen facturen die aan dit filter voldoen.',
    noObject: 'Losse factuur',
    subtotal: 'Excl. btw',
    vat: 'Btw',
    total: 'Totaal',
    issuedAt: 'Factuurdatum',
    dueAt: 'Vervaldatum',
    paidAt: 'Betaald op',
    reference: 'Kenmerk',
    keuring: (number: string) => `Keuring ${number}`,

    /* Concept bewerken */
    editDraft: 'Factuur bewerken',
    editDraftLabel: (label: string) => `Factuur ${label} bewerken`,
    draftEditable:
        'Dit is een concept. Alles eraan kun je nog wijzigen: omschrijving, aantal, bedrag, btw per regel en extra kosten. Bij versturen krijgt de factuur een nummer en een factuurdatum en ligt ze vast.',
    lineDescription: 'Omschrijving',
    lineDescriptionPlaceholder: 'Bijvoorbeeld: Bouwtechnische keuring',
    lineQuantity: 'Aantal',
    lineUnitPrice: 'Bedrag excl. btw',
    lineVat: 'Btw %',
    lineTotal: 'Regeltotaal',
    lineFromService: (service: string) => `Uit de keuring: ${service}`,
    lineNumber: (index: number) => `Regel ${index}`,
    removeLine: (index: number) => `Regel ${index} verwijderen`,
    addLine: 'Regel toevoegen',
    addLineHint: 'Voor aanvullende kosten, bijvoorbeeld een tweede bezoek of reiskosten.',
    lastLine: 'Een factuur heeft ten minste één regel.',
    referenceLabel: 'Kenmerk',
    referencePlaceholder: 'Bijvoorbeeld het ordernummer van de klant',
    notesLabel: 'Tekst op de factuur',
    notesPlaceholder: 'Een toelichting die de klant op de factuur leest.',
    paymentTerm: 'Betaaltermijn in dagen',
    paymentTermHelp: 'De vervaldatum is de factuurdatum plus dit aantal dagen.',
    amountHelp: 'Bijvoorbeeld 495 of 495,50',
    saveDraft: 'Wijzigingen opslaan',
    savingDraft: 'Opslaan…',
    draftSaved: (total: string) => `Concept opgeslagen. Nieuw totaal: ${total}.`,
    discardChanges: 'Wijzigingen verwerpen',
    newTotal: (subtotal: string, vat: string, total: string) =>
        `Excl. btw ${subtotal} · btw ${vat} · totaal ${total}`,

    /* Een verstuurde factuur ligt vast */
    lockedTitle: 'Deze factuur ligt vast',
    locked: (label: string) =>
        `Factuur ${label} is verstuurd en heeft een nummer in een doorlopende reeks. Daarom verandert er niets meer aan. Klopt er iets niet? Crediteer de factuur en stel een nieuwe op.`,
    lockedPaid: (label: string) =>
        `Factuur ${label} is betaald en ligt vast. Klopt er iets niet? Crediteer de factuur en stel een nieuwe op.`,
    lockedCredited: (label: string) =>
        `Factuur ${label} is gecrediteerd. Stel een nieuwe factuur op met de regels die wél kloppen.`,
    copyToDraft: 'Nieuwe factuur met deze regels',
    copying: 'Concept maken…',
    copied: (label: string) =>
        `${label} staat klaar als concept. Pas aan wat er anders moet en verstuur hem daarna.`,
    linesTitle: 'Regels',

    /* Meesturen met het rapport */
    withReportReady: (label: string) =>
        `Factuur ${label} staat klaar: die kun je met het rapport meesturen.`,
    withReportDraft:
        'Zolang dit een concept is, gaat er geen factuur mee met het rapport. Verstuur haar eerst.',
    withReportRendering:
        'De PDF van deze factuur wordt gemaakt; daarna kan ze met het rapport mee.',

    openPdf: 'PDF openen',
    openingPdf: 'PDF ophalen…',
    pdfNotReady:
        'De PDF van deze factuur is er nog niet. Hij wordt gemaakt zodra de factuur verstuurd is; probeer het over een minuut opnieuw.',
    pdfDraft:
        'Een concept heeft nog geen PDF. Die maken we zodra je de factuur verstuurt - het is dan onze eigen factuur-PDF.',
    pdfBlocked: 'Je browser hield het nieuwe venster tegen. De PDF staat klaar:',

    send: 'Versturen',
    sendConfirmLabel: (label: string) => `Bevestig versturen van ${label}`,
    sendConfirm: (label: string, total: string) =>
        `Factuur ${label} van ${total} krijgt een factuurnummer en een factuurdatum, en de PDF wordt gemaakt. Daarna kun je de factuur niet meer wijzigen.`,
    sendYes: 'Ja, versturen',
    sentAs: (number: string) => `Verstuurd als ${number}.`,

    markPaid: 'Markeren als betaald',
    paidConfirmLabel: (label: string) => `Bevestig betaling van ${label}`,
    paidConfirm: (label: string, total: string) =>
        `Factuur ${label} van ${total} wordt gemarkeerd als betaald. Dat komt met je naam in het logboek en kan niet worden teruggedraaid.`,
    paidYes: 'Ja, betaald',
    markedPaid: (label: string) => `${label} staat op betaald.`,

    credit: 'Crediteren',
    creditConfirmLabel: (label: string) => `Bevestig crediteren van ${label}`,
    creditConfirm: (label: string, total: string) =>
        `Factuur ${label} van ${total} krijgt de status Gecrediteerd en telt niet meer mee in de omzet van de maand. Er wordt geen creditfactuur gemaakt of verstuurd; dat regel je in je boekhouding.`,
    creditReason: 'Reden',
    creditReasonPlaceholder: 'Bijvoorbeeld: dubbel gefactureerd',
    creditReasonHelp: 'Komt met je naam in het logboek.',
    creditYes: 'Ja, crediteren',
    credited: (label: string) => `${label} is gecrediteerd.`,

    /* Tegoed */
    walletTitle: 'Tegoed',
    spendable: 'Te besteden',
    balance: 'Saldo',
    reserved: 'Gereserveerd voor lopende keuringen',
    reservedHelp:
        'Gereserveerde keuringen zijn al van het saldo af. Wordt een keuring geannuleerd, dan komen ze terug.',
    grace: 'Ruimte onder nul',
    threshold: 'Waarschuwen vanaf',
    thresholdOff: 'Uit',
    blockOnEmpty: 'Zijn je keuringen op, dan kun je pas weer nieuwe maken na een aankoop.',
    blockAfterGrace: (grace: string) =>
        `Je kunt tot ${grace} onder nul gaan; daarna kun je geen nieuwe keuringen meer aanmaken.`,
    noBlock:
        'Nieuwe keuringen worden niet tegengehouden als je keuringen op zijn; het saldo kan dan onder nul.',
    lowBalance: (balance: string, threshold: string) =>
        `Je keuringen zijn bijna op: nog ${balance}, je waarschuwt vanaf ${threshold}.`,
    lowBalanceTopUp: 'Keuringen koop je hieronder bij Keuringen kopen.',
    includedLeft: 'Inbegrepen deze maand',
    purchasedLeft: 'Gekocht, vervalt niet',

    noCheckout: 'De betaalpagina kon niet worden geopend. Er is niets betaald en niets geboekt.',

    /* Terug van de betaalpagina */
    returnChecking: 'Terug van de betaalpagina. We kijken hoe je betaling ervoor staat.',
    returnPaidTitle: 'Betaling geslaagd',
    returnPaidSubscription: (plan: string | null, included: number) =>
        `Je abonnement${plan ? ` ${plan}` : ''} is actief.${included > 0 ? ` De ${included} ${included === 1 ? 'keuring' : 'keuringen'} van deze maand staan klaar.` : ''}`,
    returnPaidInspections: (quantity: number) =>
        `${quantity} ${quantity === 1 ? 'keuring is' : 'keuringen zijn'} bijgeboekt. Gekochte keuringen vervallen niet.`,
    returnPaidOther: 'Je betaling is verwerkt.',
    returnInvoiceFollows: 'De factuur komt per mail en staat zo bij Facturen van Smartkeur.',
    returnOpenTitle: 'Betaling in behandeling',
    returnOpen:
        'De betaalprovider heeft je betaling nog niet bevestigd. Zodra dat gebeurt, wordt hij verwerkt; je hoeft niets te doen.',
    returnProcessing:
        'De betaalprovider heeft je betaling na een minuut nog niet bevestigd. Dat kan soms even duren; er is nog niets verwerkt. Controleer het later opnieuw.',
    returnFailedTitle: 'Betaling mislukt',
    returnCancelledTitle: 'Betaling afgebroken',
    returnExpiredTitle: 'Betaling verlopen',
    returnNothingHappened: (subscription: boolean) =>
        subscription
            ? 'Er is niets afgeschreven en je abonnement is niet gestart. Probeer het gerust opnieuw.'
            : 'Er is niets afgeschreven en er zijn geen keuringen bijgeboekt. Probeer het gerust opnieuw.',
    returnUnknown:
        'Terug van de betaalpagina. Hoe de betaling afliep, zie je zo bij Facturen van Smartkeur.',
    checkAgain: 'Opnieuw controleren',

    /* Grootboek */
    ledgerTitle: 'Grootboek tegoed',
    ledgerIntro: 'Elke boeking blijft staan. Een correctie is een nieuwe boeking.',
    ledgerTableLabel: 'Grootboek tegoed',
    filterType: 'Type',
    filterTypeAll: 'Alle typen',
    filterReference: 'Geboekt op',
    filterReferenceAll: 'Alles',
    filterFrom: 'Van',
    filterTo: 'Tot en met',
    columnDate: 'Datum',
    columnType: 'Type',
    columnDescription: 'Omschrijving',
    columnCredits: 'Keuringen',
    columnBalanceAfter: 'Saldo erna',
    referenceLabels: {
        order: 'Keuring',
        payment: 'Betaling',
        invoice: 'Factuur',
        tenant: 'Bedrijf',
    } satisfies Record<CreditReferenceType, string>,
    ledgerNoun: 'boekingen',
    ledgerEmpty: 'Nog geen boekingen.',
    ledgerEmptyFiltered: 'Geen boekingen die aan dit filter voldoen.',

    /* Tarieven */
    tariffsTitle: 'Tarieven per dienst',
    tariffsEmpty: 'Nog geen tarieven ingesteld.',
    inspectorFee: 'Vergoeding inspecteur',
    tariffDetails: (service: string | null, margin: string, vatRate: string) =>
        [service, `marge ${margin}`, `btw ${vatRate}`].filter(Boolean).join(' · '),
    tariffFrom: (date: string) => `vanaf ${date}`,
    tariffUntil: (date: string) => `t/m ${date}`,
    addTariff: 'Tarief toevoegen',
    tariffService: 'Dienst',
    tariffServicePick: 'Kies een dienst',
    tariffServiceMissing: 'Kies de dienst waarvoor dit tarief geldt.',
    tariffName: 'Naam',
    tariffPrice: 'Prijs excl. btw',
    tariffAmountHelp: 'Bijvoorbeeld 495 of 495,50',
    tariffVat: 'Btw in procenten',
    tariffValidFrom: 'Ingangsdatum',
    tariffValidFromHelp:
        'Leeg is vandaag. Het lopende tarief voor deze dienst stopt de dag ervoor.',
    saveTariff: 'Tarief opslaan',
    tariffSaved: (name: string) => `Tarief ${name} opgeslagen.`,
    editTariff: 'Wijzigen',
    editTariffLabel: (name: string) => `Tarief ${name} wijzigen`,
    tariffValidUntil: 'Laatste dag',
    tariffValidUntilHelp: 'Leeg is: geen einddatum.',
    tariffAmountLocked: 'Een ander bedrag is een nieuw tarief met een ingangsdatum.',
    newAmount: 'Nieuw bedrag',
    save: 'Opslaan',

    /* Maand */
    monthInvoiceCount: (n: number) => `${n} ${invoices(n)}`,
    invoiced: 'Gefactureerd',
    inspectorFees: 'Vergoeding inspecteurs',
    margin: 'Marge',
    outstanding: 'Openstaand incl. btw',
    overdue: (count: number) => `Te laat (${count})`,

    /* Abonnement bij Smartkeur */
    planTitle: 'Abonnement',
    /** Productie met de Mollie-testsleutel, voor een bedrijf in `MOLLIE_TEST_TENANTS`. */
    paymentsTestMode: 'Testmodus: betalingen zijn niet echt (Mollie-test).',
    planTrial: (remaining: number | null) =>
        remaining === null
            ? 'Je zit in de proef.'
            : remaining <= 0
              ? 'Je gratis keuringen zijn gebruikt.'
              : `Je zit in de proef: nog ${remaining} gratis ${remaining === 1 ? 'keuring' : 'keuringen'}.`,
    planIntro:
        'Alle abonnementen hebben dezelfde functies en geen limiet op gebruikers. Ze verschillen alleen in de keuringen per maand; maandelijks opzegbaar.',
    planNone:
        'Je hebt geen abonnement. Kies er een, of koop hieronder losse rapportages zonder abonnement.',
    planPerMonth: (price: string) => `${price} per maand`,
    planPerMonthInclVat: (price: string) => `${price} incl. btw`,
    planInspections: (n: number) => `${n} ${n === 1 ? 'keuring' : 'keuringen'} per maand`,
    planFeatured: 'Meest gekozen',
    planPreferred: 'Jouw keuze op de website',
    planExtras: (extra: string, single: string, singleName: string) =>
        `Extra keuring bovenop je abonnement: ${extra}. ${singleName} zonder abonnement: ${single}. Alle prijzen excl. btw.`,
    planChoose: (plan: string) => `Kies ${plan}`,
    planChooseHelp:
        'Je betaalt de eerste maand online. De maanden daarna gaan met een automatische incasso, of met een betaallink (iDEAL) die je elke maand per mail krijgt. Daarna kom je hier terug.',
    planCurrent: (plan: string) => `Je abonnement: ${plan}`,
    planThisMonth: 'Keuringen deze maand',
    planIncludedLeft: (left: number, perMonth: number) => `nog ${left} van ${perMonth} inbegrepen`,
    planIncludedExpire: (date: string) =>
        `Wat je deze maand niet gebruikt, vervalt op ${date}. Gekochte keuringen vervallen niet.`,
    planNextCharge: 'Volgende afschrijving',
    planNextPayment: 'Volgende betaling',
    planLinkInfo: 'Je betaalt per maand via een betaallink (iDEAL).',
    planLinkNext: (date: string) =>
        `Je betaalt per maand via een betaallink (iDEAL). De volgende link krijg je op ${date}.`,
    planLinkDue: (amount: string | null) =>
        `De betaling voor de volgende maand${amount ? ` (${amount} incl. btw)` : ''} staat open. Je kunt nu al betalen; de link staat ook in je mail.`,
    planPayNow: 'Nu betalen',
    planNextChargeValue: (amount: string, date: string | null) =>
        date ? `${amount} incl. btw op ${date}` : `${amount} incl. btw`,
    planNextPlan: (plan: string, date: string) => `Vanaf ${date} heb je ${plan}.`,
    planSwitchTitle: 'Wisselen',
    planSwitchHelp: (date: string) =>
        `Een ander abonnement gaat in op ${date}, aan het begin van je volgende abonnementsmaand. Deze maand blijft zoals hij is.`,
    planSwitchTo: (plan: string) => `Wissel naar ${plan}`,
    planStay: (plan: string) => `Toch ${plan} houden`,
    planSwitched: (plan: string, date: string) => `Vanaf ${date} heb je ${plan}.`,
    planCancel: 'Abonnement opzeggen',
    planCancelConfirmLabel: 'Bevestig opzeggen',
    planCancelConfirm: (date: string) =>
        `Je abonnement loopt tot ${date} en stopt dan. Er wordt daarna niets meer afgeschreven. Je bedrijf, je keuringen en je rapporten blijven staan, en je kunt daarna losse rapportages kopen.`,
    planCancelYes: 'Ja, opzeggen',
    planCancelling: (date: string) =>
        `Je abonnement is opgezegd en loopt tot ${date}. Daarna wordt er niets meer afgeschreven; je kunt dan losse rapportages kopen.`,
    planResume: 'Opzegging terugdraaien',
    planResumed: 'Je abonnement loopt gewoon door.',
    planPastDueTitle: 'De betaling van je abonnement is mislukt',
    planPastDue: (amount: string | null) =>
        `De maandelijkse incasso${amount ? ` van ${amount}` : ''} is niet gelukt. Tot je opnieuw betaalt, komen er geen nieuwe inbegrepen keuringen bij. Gekochte keuringen kun je gewoon gebruiken.`,
    planPayAgain: 'Opnieuw betalen',
    planPastDueTitleLink: 'Je abonnement is nog niet betaald',
    planPastDueLink: (amount: string | null) =>
        `De betaling${amount ? ` van ${amount}` : ''} via de betaallink is niet binnengekomen. Tot je betaalt, komen er geen nieuwe inbegrepen keuringen bij. Gekochte keuringen kun je gewoon gebruiken.`,
    planToPayment: 'Je gaat naar de betaalpagina…',

    /* Keuringen kopen */
    buyTitle: 'Keuringen kopen',
    buyIntro: (name: string, subscription: boolean) =>
        subscription
            ? `Een ${name.toLowerCase()} komt bovenop de keuringen van je abonnement en vervalt niet.`
            : `Een ${name.toLowerCase()} is één keuring met rapport, zonder abonnement. Gekochte keuringen vervallen niet.`,
    buyUnitPrice: (name: string, price: string) => `${name}: ${price} excl. btw per stuk`,
    buyQuantity: 'Aantal',
    buyLess: 'Eén minder',
    buyMore: 'Eén meer',
    buyExclVat: 'Excl. btw',
    buyVat: (percent: number) => `Btw ${percent}%`,
    buyTotal: 'Totaal incl. btw',
    buySubmit: 'Naar de betaling',
    buyHelp:
        'Je betaalt online, bijvoorbeeld met iDEAL. De keuringen worden bijgeboekt zodra de betaling bevestigd is.',

    /* Betalingen aan Smartkeur */
    paymentsTitle: 'Betalingen aan Smartkeur',
    paymentsTableLabel: 'Betalingen aan Smartkeur',
    paymentsEmpty: 'Nog geen betalingen.',
    paymentsNoun: 'betalingen',
    columnPaymentDate: 'Datum',
    columnPaymentDescription: 'Omschrijving',
    columnPaymentAmount: 'Bedrag incl. btw',
    columnPaymentStatus: 'Status',
    columnPaymentInvoice: 'Factuur',
    invoicePdf: (number: string) => `${number} (PDF)`,
    invoicePdfLabel: (number: string) => `Factuur ${number} als PDF openen`,
    invoiceRendering: 'Wordt gemaakt',
    invoiceNone: '—',
};

/**
 * De tabbladen van Facturatie, met per tabblad één zin over wat er staat. De
 * eerste twee gaan over wat het bedrijf aan Smartkeur betaalt, de laatste twee
 * over wat zijn klanten aan het bedrijf betalen; de namen houden die twee uit
 * elkaar.
 */
export const billingTabsCopy = {
    plan: {
        label: 'Abonnement & tegoed',
        intro: 'Wat je bij Smartkeur afneemt: je abonnement, je tegoed aan keuringen en extra keuringen.',
    },
    platformInvoices: {
        label: 'Facturen van Smartkeur',
        intro: 'Wat je aan Smartkeur hebt betaald, met de facturen als PDF, en elke boeking op je tegoed.',
    },
    invoices: {
        label: 'Facturen aan klanten',
        intro: 'Wat je klanten aan jou betalen: je facturen voor keuringen en je omzet van deze maand.',
    },
    tariffs: {
        label: 'Tarieven',
        intro: 'Wat je je klanten per dienst rekent, en wat de inspecteur daarvan krijgt.',
    },
} as const satisfies Record<BillingTabKey, { label: string; intro: string }>;
